// Storm cards for the Tropical workspace: a small basemap thumbnail that answers
// WHERE at a glance, a classification chip, and a compact fact line. Clicking a
// card flies the map to the system.
//
// The thumbnail is deliberately one tile: the Esri ocean base tile (the same
// basemap the map itself draws) at z=3 containing the system's position, scaled
// into a small canvas with the position marked in the house monoline style
// (dot + halo, like mapicons.ts). One tile means one cacheable fetch per ocean
// region, no projection gymnastics, and a graceful failure mode — a blank
// sea-coloured square with the mark still on it is still a usable card.

import { tileAt, type StormChip } from "../data/stormlist.js";

// Same service as map.ts's basemap — the thumbnails match the map by construction.
const OCEAN_TILE = (z: number, x: number, y: number): string =>
  `https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/${z}/${y}/${x}`;

const THUMB_ZOOM = 3; // one tile = 45° of longitude: the basin, not the whole world
const THUMB_CSS = 72; // px; backing store is 2× for retina

// One inflight/settled promise per tile URL. Several storms in one basin (the
// common case in season) share a single fetch, and re-renders cost nothing.
const tileCache = new Map<string, Promise<HTMLImageElement>>();

function fetchTile(url: string): Promise<HTMLImageElement> {
  const hit = tileCache.get(url);
  if (hit !== undefined) return hit;
  const p = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`tile failed: ${url}`));
    img.src = url;
  });
  // A failed fetch is not cached as failure forever — evict so a later render
  // (next site move, next session of a flaky link) can retry.
  p.catch(() => tileCache.delete(url));
  tileCache.set(url, p);
  return p;
}

function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v !== "" ? v : fallback;
}

function drawMark(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  kind: "storm" | "outlook",
  color: string,
): void {
  const halo = cssVar("--icon-halo", "#ffffff");
  const r = kind === "storm" ? 9 : 11;
  ctx.beginPath();
  ctx.arc(px, py, r, 0, Math.PI * 2);
  if (kind === "storm") {
    ctx.fillStyle = color;
    ctx.strokeStyle = halo;
    ctx.lineWidth = 4;
    ctx.stroke(); // halo under the fill, so the dot reads on any sea
    ctx.fill();
  } else {
    // Hollow ring: a watched area, not a positioned system.
    ctx.strokeStyle = halo;
    ctx.lineWidth = 7;
    ctx.stroke();
    ctx.strokeStyle = color;
    ctx.lineWidth = 3.5;
    ctx.stroke();
  }
}

/** The thumbnail canvas: fallback ground + mark immediately, tile when it lands. */
function thumb(lat: number, lon: number, kind: "storm" | "outlook", color: string): HTMLCanvasElement {
  const c = document.createElement("canvas");
  const size = THUMB_CSS * 2;
  c.width = size;
  c.height = size;
  c.className = "tropthumb";
  const ctx = c.getContext("2d");
  if (ctx === null) return c;
  const t = tileAt(lat, lon, THUMB_ZOOM);
  const px = t.fx * size;
  const py = t.fy * size;
  const ground = (): void => {
    ctx.fillStyle = cssVar("--surface-2", "#e7ebe9");
    ctx.fillRect(0, 0, size, size);
  };
  ground();
  drawMark(ctx, px, py, kind, color);
  fetchTile(OCEAN_TILE(t.z, t.x, t.y)).then(
    (img) => {
      ctx.drawImage(img, 0, 0, size, size);
      drawMark(ctx, px, py, kind, color);
    },
    () => {
      // Blank sea stays; the mark is already there. The card still answers "where".
    },
  );
  return c;
}

export interface TropCardOpts {
  /** Card title: the storm's name, or "Atlantic development area". */
  name: string;
  chip: StormChip | null;
  /** Mono fact line: "40 kt · moving E 18 kt", "80% in 7 days". "" hides it. */
  fact: string;
  /** Quiet trailing note: "cone and track on the map", "JTWC · West Pacific". */
  note: string;
  lat: number | null;
  lon: number | null;
  marker: "storm" | "outlook";
  /** Fly the map to the system. Omitted (or no position) = a static card. */
  onFly?: () => void;
}

/** Build one storm/outlook card. A <button> when it can fly, a <div> when not. */
export function tropCardEl(o: TropCardOpts): HTMLElement {
  const canFly = o.onFly !== undefined && o.lat !== null && o.lon !== null;
  const card = document.createElement(canFly ? "button" : "div");
  if (card instanceof HTMLButtonElement) {
    card.type = "button";
    card.title = `Fly the map to ${o.name}`;
    card.addEventListener("click", () => o.onFly?.());
  }
  card.className = "tropcard";
  if (o.lat !== null && o.lon !== null) {
    const color =
      o.chip?.level === "crit"
        ? cssVar("--crit", "#b23b3b")
        : o.chip?.level === "warn"
          ? cssVar("--warn", "#b9791a")
          : cssVar("--navy", "#16232e");
    card.appendChild(thumb(o.lat, o.lon, o.marker, color));
  }
  const txt = document.createElement("div");
  txt.className = "tc-txt";
  const head = document.createElement("div");
  head.className = "tc-head";
  if (o.chip !== null) {
    const chip = document.createElement("span");
    chip.className = `chip tc-chip${o.chip.level !== "none" ? ` ${o.chip.level}` : ""}`;
    chip.textContent = o.chip.short;
    head.appendChild(chip);
  }
  const name = document.createElement("span");
  name.className = "tc-name";
  name.textContent = o.name;
  head.appendChild(name);
  txt.appendChild(head);
  if (o.fact !== "") {
    const f = document.createElement("div");
    f.className = "tc-fact";
    f.textContent = o.fact;
    txt.appendChild(f);
  }
  if (o.note !== "") {
    const n = document.createElement("div");
    n.className = "tc-note";
    n.textContent = o.note;
    txt.appendChild(n);
  }
  card.appendChild(txt);
  return card;
}
